import { Breadcrumbs } from '@/components/Breadcrumbs/Breadcrumbs';
import { Container } from '@/components/Container/Container';
import { IconDoc } from '@/components/Icons/IconDoc';
import { PublicPage } from '@/components/Page/PublicPage';
import { ResourceListItem } from '@/components/ResourceListItem/ResourceListItem';
import SkeletonLoader from '@/components/SkeletonLoader/SkeletonLoader';
import { TagGroupCard } from '@/components/TagGroupCard/TagGroupCard';
import { CONSTANTS } from '@/constants';
import { useProcessState } from '@/hooks/useProcessState/useProcessState';
import { SEARCH_DIRECTORIES_WITH_FILES_QUERY } from '@/hooks/useSearchDirectoriesWithFiles/useSearchDirectoriesWithFiles';
import { ESize } from '@/models/enums';
import { environmentVariables } from '@/utils/environmentVariables';
import { getEkardoApolloClient } from '@netfront/common-library';
import {
  CMSContentPage,
  GET_CONTENT_PAGE_BY_URL_QUERY,
  IContentPage,
  IDirectory,
  IGetContentPageByUrlQueryGraphQLResponse,
  IGetContentPageByUrlQueryVariables,
  useContentPageContext,
  useSearchDirectories,
} from '@netfront/ekardo-content-library';
import { Button } from '@netfront/ui-library';
import { GetStaticProps, NextPage } from 'next';
import Image from 'next/image';
import { useEffect, useState } from 'react';

// Tag names as set up by content editors in the CMS for the FOR_CLINICIANS directory (id 60).
// Each directory item carries exactly one linked tag — that tag decides which section of the page it renders in.
const TAG_NAME = {
  FACT_SHEETS: 'Fact Sheets',
  DAWN_PROGRAM_FLYER: 'DAWN Program Flyer',
  LIVING_WELL_WITH_DIABETES: 'Living Well With Diabetes',
  ECLIPSE_FLYER: 'eClipse Flyer',
};

const ECLIPSE_PROGRAMS = [
  { id: 'more', title: 'MoRE', description: 'Mood, resilience & emotional wellbeing', href: '#' },
  { id: 'shade', title: 'SHADE', description: 'Self-help for alcohol, other drugs & depression', href: '#' },
];

const groupItemsByTagName = (items: IDirectory[]) =>
  items.reduce<Record<string, IDirectory[]>>((groups, item) => {
    const tagName = item.linkedTags?.[0]?.name;
    if (!tagName) {
      return groups;
    }

    return {
      ...groups,
      [tagName]: [...(groups[tagName] ?? []), item],
    };
  }, {});

const getDownloadUrl = (item: IDirectory) => {
  const filePresignedUrl = (item as any).files?.find((f: any) => f?.asset?.presignedUrl)?.asset?.presignedUrl;
  return filePresignedUrl ?? item.asset?.presignedUrl ?? item.url ?? '#';
};

const stripHtml = (html: string) => html.replace(/<[^>]*>/g, '').trim();

type ForCliniciansNextPageProps = {
  contentPage: IContentPage | null;
};

const ForCliniciansNextPage: NextPage<ForCliniciansNextPageProps> = ({ contentPage }) => {
  const { start, complete, fail, isCompleted, isLoading } = useProcessState();
  const [resources, setResources] = useState<IDirectory[]>([]);
  const { dispatch } = useContentPageContext();

  const { handleSearchDirectories } = useSearchDirectories({
    onCompleted: ({ directories }) => {
      setResources(directories);
      complete();
    },
    onError: () => {
      fail();
    },
    query: SEARCH_DIRECTORIES_WITH_FILES_QUERY,
  });

  useEffect(() => {
    start();
    handleSearchDirectories({
      directoryTypeId: CONSTANTS.DIRECTORY.FOR_CLINICIANS.ID,
      projectId: String(process.env.REACT_APP_PROJECT_ID),
    });
  }, []);

  useEffect(() => {
    if (!contentPage) {
      return;
    }

    dispatch({
      payload: {
        contentPage,
      },
      type: 'loadContentPage',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentPage?.id]);

  const itemsByTag = groupItemsByTagName(resources);
  const factSheetItems = itemsByTag[TAG_NAME.FACT_SHEETS] ?? [];
  const featuredHandoutItems = itemsByTag[TAG_NAME.DAWN_PROGRAM_FLYER] ?? [];
  const generalHandoutItems = itemsByTag[TAG_NAME.LIVING_WELL_WITH_DIABETES] ?? [];
  const eclipseFlyerItems = itemsByTag[TAG_NAME.ECLIPSE_FLYER] ?? [];

  return (

    
    <PublicPage
      meta={{
        seoDescription: 'Resources and information for clinicians supporting people with diabetes and mental health.',
        seoTitle: 'For clinicians',
      }}
      title="For clinicians"
    >
      {contentPage && (
        <Container additionalClassNames="pt-12">
          <div className="mb-2">
            <Breadcrumbs items={[{ content: <span>For clinicians</span>, key: 'for-clinicians' }]} />
          </div>
          <CMSContentPage projectId={environmentVariables.projectId} />
        </Container>
      )}

      <Container additionalClassNames="py-12" size={ESize.MD}>


        {/* Fact sheets */}
        <div className="mb-12">
          <h2 className="mb-2">Fact sheets</h2>
          <p className="color-grey-600 mb-6">
            Simple guides for noticing and responding to patients&apos; mental health in your day-to-day work.
          </p>

          {isLoading && (
            <div className="grid gap-4 md:grid-cols-2">
              <SkeletonLoader additionalClassNames="h-64 rounded-lg" />
              <SkeletonLoader additionalClassNames="h-64 rounded-lg" />
            </div>
          )}

          {isCompleted && factSheetItems.length > 0 && (
            <div className="grid gap-4 grid-cols-1">
              <TagGroupCard itemsLayout="grid" name="Fact sheets" items={factSheetItems} />
            </div>
          )}

          {isCompleted && factSheetItems.length === 0 && (
            <p className="color-grey-600">No fact sheets found.</p>
          )}
        </div>

        {/* Handouts for patients */}
        <div className="mb-12">
          <h2 className="mb-2">Handouts for your patients</h2>
          <p className="color-grey-600 mb-6">Print or share these directly with the people you work with.</p>

          {isCompleted && featuredHandoutItems.length > 0 && (
            <div className="flex flex-col gap-6 mb-6">
              {featuredHandoutItems.map((item) => (
                <div
                  key={item.id}
                  className="shadow rounded-lg overflow-hidden bg-secondary flex flex-col lg:flex-row lg:items-center"
                >
                  <div className="shrink-0" style={{ width: 120, height: 160 }}>
                    {item.asset?.presignedUrl ? (
                      <img
                        alt={item.title}
                        className="w-full h-full object-cover"
                        src={item.asset.presignedUrl}
                      />
                    ) : (
                      <div
                        className="w-full h-full bg-grey-300 flex flex-col items-center justify-center gap-2"
                      >
                        <IconDoc className="color-grey-600" />
                        <p className="color-grey-600 text-xs text-center mb-0" style={{ fontFamily: 'monospace' }}>
                          {item.title}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 flex flex-col gap-4 p-6">
                    <div>
                      <p className="text-uppercase text-xs font-bold color-grey-600 mb-2" style={{ letterSpacing: '0.05em' }}>
                        Introduce DAWN
                      </p>
                      <h3 className="mb-2">{item.title}</h3>
                      {item.description && <p className="color-grey-600 mb-0">{stripHtml(item.description)}</p>}
                    </div>
                    <Button
                      additionalClassNames="self-start"
                      linkButton={{ id: `download-flyer-${item.id}`, url: getDownloadUrl(item), target: '_blank' }}
                      text="Download flyer (PDF)"
                      variant="accent"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {isCompleted && generalHandoutItems.length > 0 && (
            <>
              <p className="text-uppercase text-xs font-bold color-grey-600 mb-4" style={{ letterSpacing: '0.05em' }}>
                Living well with diabetes
              </p>
              <div className="grid gap-4 md:grid-cols-3">
                {generalHandoutItems.map((item) => (
                  <ResourceListItem
                    key={item.id}
                    description={item.description}
                    href={getDownloadUrl(item)}
                    subTitle={item.subTitle}
                    title={item.title}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* eCliPSE portal */}
        <div className="shadow rounded-lg overflow-hidden flex flex-col lg:flex-row lg:items-center">
          <div className="w-full lg:w-2/5 shrink-0 p-6 flex items-center justify-center">
            <Image
              alt="The eCliPSE digital ecosystem — connected mental health programs"
              height={896}
              src="/images/eclipse-ecosystem.png"
              style={{ height: 'auto', width: '100%' }}
              width={1841}
            />
          </div>
          <div className="flex-1 p-8">
            <p className="text-uppercase text-xs font-bold color-grey-600 mb-2" style={{ letterSpacing: '0.05em' }}>
              Beyond DAWN
            </p>
            <h2 className="mb-2">The eCliPSE portal</h2>
            <p className="color-grey-600 mb-6" style={{ maxWidth: '34rem' }}>
              eCliPSE is a portal of free, evidence-based digital mental health programs. Two are especially
              relevant for people living with diabetes:
            </p>
            <div className="flex flex-col gap-3 mb-6">
              {ECLIPSE_PROGRAMS.map(({ id, title, description, href }) => (
                <a
                  key={id}
                  className="flex items-center gap-4 shadow rounded-lg p-4 no-underline color-black hover:shadow-lg transition-150"
                  href={href}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm mb-0 leading-tight">{title}</p>
                    <p className="color-grey-600 text-sm mb-0 mt-1">{description}</p>
                  </div>
                </a>
              ))}
            </div>
            {eclipseFlyerItems.length > 0 && (
              <div className="flex flex-wrap gap-3">
                {eclipseFlyerItems.map((item) => (
                  <Button
                    key={item.id}
                    linkButton={{ id: `eclipse-flyer-${item.id}`, url: getDownloadUrl(item), target: '_blank' }}
                    text={item.title}
                    variant="accent"
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </Container>
    </PublicPage>
  );
};

export const getStaticProps: GetStaticProps<ForCliniciansNextPageProps> = async () => {
  const client = getEkardoApolloClient({
    product: 'EKARDO',
  });

  try {
    const result = await client.query<IGetContentPageByUrlQueryGraphQLResponse, IGetContentPageByUrlQueryVariables>({
      query: GET_CONTENT_PAGE_BY_URL_QUERY,
      variables: {
        contentPageUrl: environmentVariables.forCliniciansPageCmsUrl,
        projectId: environmentVariables.projectId,
      },
    });

    return {
      props: {
        contentPage: result.data.contentPage.getContentPageByUrl,
      },
      revalidate: CONSTANTS.STATIC_PAGE_REGENERATION_TIME_IN_SECONDS,
    };
  } catch (error) {
    // The CMS content block for this page is optional — fall back to the hand-built sections below if it's missing.
    console.error('Failed to fetch CMS content page for for-clinicians:', error, JSON.stringify(error));

    return {
      props: {
        contentPage: null,
      },
      revalidate: CONSTANTS.STATIC_PAGE_REGENERATION_TIME_IN_SECONDS,
    };
  }
};

export default ForCliniciansNextPage;
